// Show minimal navbar on tracker.dvamina.com
const IS_TRACKER = typeof window !== "undefined" && window.location.hostname === "tracker.dvamina.com";
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { User } from 'lucide-react';
import { BoltIcon } from '@heroicons/react/24/outline';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [hoveredLang, setHoveredLang] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const languages = [
    { code: 'en', label: 'EN', flag: '/assets/flags/gb.png' },
    { code: 'bg', label: 'BG', flag: '/assets/flags/bg.png' },
    { code: 'fr', label: 'FR', flag: '/assets/flags/fr.png' },
    { code: 'it', label: 'IT', flag: '/assets/flags/it.png' },
    { code: 'de', label: 'DE', flag: '/assets/flags/de.png' },
  ];

  const switchLanguage = (lang) => {
    i18n.changeLanguage(lang);
    setShowDropdown(false);
  };

  // Show simplified navbar on tracker domain OR when on admin pages (logged in)
  const isAdminPage = location.pathname.startsWith('/admin');
  const isLoggedIn = localStorage.getItem('jwtToken') && localStorage.getItem('user');
  
  if (IS_TRACKER || (isAdminPage && isLoggedIn)) {
    return (
      <nav className='bg-white shadow-md sticky top-0 z-50'>
        <div className='container mx-auto px-6 py-1 flex items-center justify-between'>
          {/* Logo */}
          <div className='flex items-center space-x-4'>
            <Link to='/'><img src='/logo.jpeg' alt='Infinity Energy Logo' className='w-[150px]' /></Link>
          </div>
          {/* Center message */}
          <div className="flex-1 text-center font-semibold text-2xl text-green-800">
            Your future, powered by the Sun!
          </div>
          {/* Language Dropdown, LinkedIn, Instagram, Login */}
          <div className='relative'>
            <div className="flex items-center gap-2 items-baseline">
              <div className='relative'>
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className='text-[#173861] text-2xl focus:outline-none'
                >
                  <div className="w-[26px] h-[26px] rounded-md overflow-hidden flex items-center justify-center bg-white">
                    <img
                      src={(languages.find(l => l.code === i18n.language) || languages[0]).flag}
                      alt="Language flag"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </button>
                {showDropdown && (
                  <div className='absolute top-full left-0 mt-2 bg-white border rounded shadow-md z-10'>
                    <ul className='text-[#173861]'>
                      {languages.map(({ code, label, flag }) => (
                        <li key={code}>
                          <div
                            className="flex items-center gap-2"
                            onMouseEnter={() => setHoveredLang(code)}
                            onMouseLeave={() => setHoveredLang(null)}
                          >
                            <button
                              onClick={() => switchLanguage(code)}
                              className='block px-4 py-2 hover:bg-gray-100 w-full text-left'
                            >
                              {hoveredLang === code
                                ? <div className="w-[26px] h-[26px] rounded-md overflow-hidden flex items-center justify-center bg-white">
                                    <img src={flag} alt={`${label} flag`} className="w-full h-full object-cover" />
                                  </div>
                                : label}
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <a
                href="https://www.linkedin.com/company/105688845"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <div className="w-[26px] h-[26px] rounded-md overflow-hidden flex items-center justify-center bg-white">
                  <img
                    src="/assets/flags/linked.png"
                    alt="LinkedIn"
                    className="w-full h-full object-cover"
                  />
                </div>
              </a>
              <a
                href="https://www.instagram.com/infisolarrazlog/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <div className="w-[26px] h-[26px] rounded-md overflow-hidden flex items-center justify-center bg-white">
                  <img
                    src="/assets/flags/insta.png"
                    alt="Instagram"
                    className="w-full h-full object-cover"
                  />
                </div>
              </a>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  const scrollToSection = (sectionId) => {
    // Check if we're on a standalone page (not home page)
    const isStandalonePage = location.pathname !== '/' && !location.pathname.startsWith('/#');
    
    if (isStandalonePage) {
      // Navigate to home page first, then scroll to section
      navigate('/');
      // Wait for navigation and page to be ready, then scroll
      setTimeout(() => {
        const scrollToElement = () => {
          const element = document.getElementById(sectionId);
          if (element) {
            const offset = 150;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
            setTimeout(() => {
              setActiveSection(sectionId);
            }, 100);
          } else {
            // If element not found yet, try again after a short delay
            setTimeout(scrollToElement, 50);
          }
        };
        scrollToElement();
      }, 200);
    } else {
      // We're on the home page, just scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = 150; // Account for fixed navbar height
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
        // Update active section immediately after scrolling starts
        setTimeout(() => {
          setActiveSection(sectionId);
        }, 100);
      }
    }
  };

  const getCurrentSection = () => {
    const sections = ['home', 'news', 'services', 'machines', 'certificates', 'projects', 'why-infinity', 'contact'];
    const scrollPosition = window.scrollY + 150;
    
    for (let i = sections.length - 1; i >= 0; i--) {
      const element = document.getElementById(sections[i]);
      if (element) {
        const elementTop = element.offsetTop;
        const elementBottom = elementTop + element.offsetHeight;
        if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
          return sections[i];
        }
      }
    }
    return 'home';
  };

  const [activeSection, setActiveSection] = useState('home');
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Check if we're on a standalone page (not Home page)
  const isStandalonePage = location.pathname !== '/' && !location.pathname.startsWith('/#');

  useEffect(() => {
    const handleScroll = () => {
      setActiveSection(getCurrentSection());
      // Use a lower threshold for smoother transition
      setIsScrolled(window.scrollY > 20);
    };
    // Check initial scroll position
    setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // On standalone pages, always show white background
  const shouldShowWhiteBg = isStandalonePage || isScrolled;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-in-out ${
      shouldShowWhiteBg 
        ? 'bg-white/70 backdrop-blur-md border-b border-slate-200/50 shadow-sm' 
        : 'bg-transparent border-b border-transparent'
    }`}>
      <div className="container mx-auto px-6 py-2 md:py-6 flex flex-col md:flex-row items-center justify-between gap-2 md:gap-4">
        {/* Mobile: Hamburger menu on left, Logo centered */}
        <div className="w-full md:w-auto flex items-center justify-between md:justify-start">
          {/* Hamburger menu - mobile only, left side */}
          <div className="flex md:hidden items-center">
            <button onClick={() => setShowMobileMenu(!showMobileMenu)} className={`focus:outline-none transition-colors p-2 rounded-lg ${
              shouldShowWhiteBg 
                ? 'text-slate-900 hover:bg-slate-100' 
                : 'text-white hover:bg-white/10'
            }`} aria-label="Toggle menu">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
          
          {/* Logo - centered on mobile, left on desktop */}
          <div className="flex-1 md:flex-none flex justify-center md:justify-start">
            <a href="#home" onClick={(e) => { 
              e.preventDefault(); 
              if (location.pathname !== '/' && !location.pathname.startsWith('/#')) {
                navigate('/');
              } else {
                scrollToSection('home');
              }
            }} className="flex items-center">
              <div className="relative">
                <div className={`w-16 h-16 md:w-20 md:h-20 rounded-full backdrop-blur-md shadow-2xl border-2 flex items-center justify-center p-2 hover:scale-105 transition-all duration-300 ${
                  shouldShowWhiteBg 
                    ? 'bg-white border-slate-200' 
                    : 'bg-white border-white/50'
                }`}>
                  <img src="/logo.jpeg" alt="Infinity Energy Logo" className="w-full h-full object-contain rounded-full" />
                </div>
              </div>
            </a>
          </div>
          
          {/* Spacer for mobile to balance hamburger */}
          <div className="w-11 md:hidden"></div>
        </div>

        {/* Center Nav Links - tablet and desktop only */}
        <div className={`hidden md:flex flex-wrap justify-center gap-2 md:gap-3 font-semibold text-sm md:text-base text-center transition-colors ${
          shouldShowWhiteBg ? 'text-slate-900' : 'text-white'
        }`}>
          <button onClick={() => scrollToSection('home')} className={`transition-all rounded-lg px-3 py-2 md:px-4 md:py-2 ${
            activeSection === 'home' 
              ? 'bg-gradient-to-r from-emerald-400 to-emerald-600 text-white shadow-lg' 
              : shouldShowWhiteBg 
                ? 'hover:text-emerald-600 hover:bg-emerald-50' 
                : 'hover:text-emerald-300 hover:bg-white/10'
          }`}>{t('home')}</button>
          <button onClick={() => scrollToSection('services')} className={`transition-all rounded-lg px-3 py-2 md:px-4 md:py-2 ${
            activeSection === 'services' 
              ? 'bg-gradient-to-r from-emerald-400 to-emerald-600 text-white shadow-lg' 
              : shouldShowWhiteBg 
                ? 'hover:text-emerald-600 hover:bg-emerald-50' 
                : 'hover:text-emerald-300 hover:bg-white/10'
          }`}>{t('services')}</button>
          <button onClick={() => scrollToSection('machines')} className={`transition-all rounded-lg px-3 py-2 md:px-4 md:py-2 ${
            activeSection === 'machines' 
              ? 'bg-gradient-to-r from-emerald-400 to-emerald-600 text-white shadow-lg' 
              : shouldShowWhiteBg 
                ? 'hover:text-emerald-600 hover:bg-emerald-50' 
                : 'hover:text-emerald-300 hover:bg-white/10'
          }`}>{t('machines')}</button>
          <button onClick={() => scrollToSection('certificates')} className={`transition-all rounded-lg px-3 py-2 md:px-4 md:py-2 ${
            activeSection === 'certificates' 
              ? 'bg-gradient-to-r from-emerald-400 to-emerald-600 text-white shadow-lg' 
              : shouldShowWhiteBg 
                ? 'hover:text-emerald-600 hover:bg-emerald-50' 
                : 'hover:text-emerald-300 hover:bg-white/10'
          }`}>{t('certificates')}</button>
          <button onClick={() => scrollToSection('projects')} className={`transition-all rounded-lg px-3 py-2 md:px-4 md:py-2 ${
            activeSection === 'projects' 
              ? 'bg-gradient-to-r from-emerald-400 to-emerald-600 text-white shadow-lg' 
              : shouldShowWhiteBg 
                ? 'hover:text-emerald-600 hover:bg-emerald-50' 
                : 'hover:text-emerald-300 hover:bg-white/10'
          }`}>{t('projects')}</button>
          <button onClick={() => scrollToSection('why-infinity')} className={`whitespace-nowrap transition-all rounded-lg px-3 py-2 md:px-4 md:py-2 ${
            activeSection === 'why-infinity' 
              ? 'bg-gradient-to-r from-emerald-400 to-emerald-600 text-white shadow-lg' 
              : shouldShowWhiteBg 
                ? 'hover:text-emerald-600 hover:bg-emerald-50' 
                : 'hover:text-emerald-300 hover:bg-white/10'
          }`}>{t('whyInfinity')}</button>
          <button onClick={() => scrollToSection('contact')} className={`transition-all rounded-lg px-3 py-2 md:px-4 md:py-2 ${
            activeSection === 'contact' 
              ? 'bg-gradient-to-r from-emerald-400 to-emerald-600 text-white shadow-lg' 
              : shouldShowWhiteBg 
                ? 'hover:text-emerald-600 hover:bg-emerald-50' 
                : 'hover:text-emerald-300 hover:bg-white/10'
          }`}>{t('contact')}</button>
        </div>

        <div className="w-full md:w-auto flex justify-center md:justify-end gap-3 mt-2 md:mt-0">
          {/* Desktop icons - tablet and desktop only */}
          <div className='relative hidden md:flex items-center gap-3 items-baseline'>
            <div className='relative'>
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className={`text-2xl focus:outline-none rounded-full border shadow-sm hover:shadow-md transition ${
                  shouldShowWhiteBg 
                    ? 'text-slate-900 border-slate-200 hover:border-emerald-400'
                    : 'text-white border-white/30 hover:border-emerald-400'
                }`}
                aria-label="Select Language"
              >
                <div className={`w-[32px] h-[32px] rounded-full overflow-hidden flex items-center justify-center border ${
                  shouldShowWhiteBg 
                    ? 'bg-white border-slate-200' 
                    : 'bg-white/20 backdrop-blur-sm border-white/30'
                }`}>
                  <img
                    src={(languages.find(l => l.code === i18n.language) || languages[0]).flag}
                    alt="Language flag"
                    className="w-full h-full object-cover"
                  />
                </div>
              </button>
              {showDropdown && (
                <div className='absolute top-full left-0 mt-2 bg-white border border-slate-200 rounded shadow-lg z-20 min-w-[120px] animate-fade-in'>
                  <ul className='text-slate-900'>
                    {languages.map(({ code, label, flag }) => (
                      <li key={code}>
                        <div
                          className="flex items-center gap-2"
                          onMouseEnter={() => setHoveredLang(code)}
                          onMouseLeave={() => setHoveredLang(null)}
                        >
                          <button
                            onClick={() => switchLanguage(code)}
                            className='block px-4 py-2 hover:bg-emerald-50 w-full text-left flex items-center gap-2 rounded-lg transition'
                          >
                            {hoveredLang === code
                              ? <div className="w-[26px] h-[26px] rounded-md overflow-hidden flex items-center justify-center bg-white">
                                  <img src={flag} alt={`${label} flag`} className="w-full h-full object-cover" />
                                </div>
                              : label}
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <a
              href="https://www.linkedin.com/company/105688845"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="hover:scale-110 transition"
            >
              <div className="w-[32px] h-[32px] rounded-full overflow-hidden flex items-center justify-center bg-white border border-gray-200 shadow-sm">
                <img
                  src="/assets/flags/linked.png"
                  alt="LinkedIn"
                  className="w-full h-full object-cover"
                />
              </div>
            </a>
            <a
              href="https://www.instagram.com/infisolarrazlog/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="hover:scale-110 transition"
            >
              <div className="w-[32px] h-[32px] rounded-full overflow-hidden flex items-center justify-center bg-white border border-gray-200 shadow-sm">
                <img
                  src="/assets/flags/insta.png"
                  alt="Instagram"
                  className="w-full h-full object-cover"
                />
              </div>
            </a>
          </div>
        </div>

      </div>

      {/* Mobile nav links */}
      {showMobileMenu && (
        <div className="flex flex-col mt-4 space-y-2 font-semibold text-base md:hidden px-6 pb-4 animate-fade-in bg-white/95 backdrop-blur-xl text-slate-900 rounded-lg mx-4 mt-2 shadow-lg">
          <button onClick={() => { scrollToSection('home'); setShowMobileMenu(false); }} className={`transition-all rounded-lg px-4 py-2 text-left ${
            activeSection === 'home' 
              ? 'bg-gradient-to-r from-emerald-400 to-emerald-600 text-white shadow-lg' 
              : shouldShowWhiteBg 
                ? 'hover:text-emerald-600 hover:bg-emerald-50' 
                : 'hover:text-emerald-300 hover:bg-white/10'
          }`}>{t('home')}</button>
          <button onClick={() => { scrollToSection('services'); setShowMobileMenu(false); }} className={`transition-all rounded-lg px-4 py-2 text-left ${
            activeSection === 'services' 
              ? 'bg-gradient-to-r from-emerald-400 to-emerald-600 text-white shadow-lg' 
              : shouldShowWhiteBg 
                ? 'hover:text-emerald-600 hover:bg-emerald-50' 
                : 'hover:text-emerald-300 hover:bg-white/10'
          }`}>{t('services')}</button>
          <button onClick={() => { scrollToSection('machines'); setShowMobileMenu(false); }} className={`transition-all rounded-lg px-4 py-2 text-left ${
            activeSection === 'machines' 
              ? 'bg-gradient-to-r from-emerald-400 to-emerald-600 text-white shadow-lg' 
              : shouldShowWhiteBg 
                ? 'hover:text-emerald-600 hover:bg-emerald-50' 
                : 'hover:text-emerald-300 hover:bg-white/10'
          }`}>{t('machines')}</button>
          <button onClick={() => { scrollToSection('certificates'); setShowMobileMenu(false); }} className={`transition-all rounded-lg px-4 py-2 text-left ${
            activeSection === 'certificates' 
              ? 'bg-gradient-to-r from-emerald-400 to-emerald-600 text-white shadow-lg' 
              : shouldShowWhiteBg 
                ? 'hover:text-emerald-600 hover:bg-emerald-50' 
                : 'hover:text-emerald-300 hover:bg-white/10'
          }`}>{t('certificates')}</button>
          <button onClick={() => { scrollToSection('projects'); setShowMobileMenu(false); }} className={`transition-all rounded-lg px-4 py-2 text-left ${
            activeSection === 'projects' 
              ? 'bg-gradient-to-r from-emerald-400 to-emerald-600 text-white shadow-lg' 
              : shouldShowWhiteBg 
                ? 'hover:text-emerald-600 hover:bg-emerald-50' 
                : 'hover:text-emerald-300 hover:bg-white/10'
          }`}>{t('projects')}</button>
          <button onClick={() => { scrollToSection('why-infinity'); setShowMobileMenu(false); }} className={`transition-all rounded-lg px-4 py-2 text-left ${
            activeSection === 'why-infinity' 
              ? 'bg-gradient-to-r from-emerald-400 to-emerald-600 text-white shadow-lg' 
              : shouldShowWhiteBg 
                ? 'hover:text-emerald-600 hover:bg-emerald-50' 
                : 'hover:text-emerald-300 hover:bg-white/10'
          }`}>{t('whyInfinity')}</button>
          <button onClick={() => { scrollToSection('contact'); setShowMobileMenu(false); }} className={`transition-all rounded-lg px-4 py-2 text-left ${
            activeSection === 'contact' 
              ? 'bg-gradient-to-r from-emerald-400 to-emerald-600 text-white shadow-lg' 
              : shouldShowWhiteBg 
                ? 'hover:text-emerald-600 hover:bg-emerald-50' 
                : 'hover:text-emerald-300 hover:bg-white/10'
          }`}>{t('contact')}</button>
          
          {/* Language selector and social icons in menu */}
          <div className="pt-4 mt-4 border-t border-slate-200/50 flex items-center justify-center gap-4">
            <div className='relative'>
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className='text-[#173861] text-2xl focus:outline-none rounded-full border border-gray-200 shadow-sm hover:shadow-md transition'
                aria-label="Select Language"
              >
                <div className="w-[32px] h-[32px] rounded-full overflow-hidden flex items-center justify-center bg-white">
                  <img
                    src={(languages.find(l => l.code === i18n.language) || languages[0]).flag}
                    alt="Language flag"
                    className="w-full h-full object-cover"
                  />
                </div>
              </button>
              {showDropdown && (
                <div className='absolute top-full left-1/2 transform -translate-x-1/2 mt-2 bg-white border rounded shadow-lg z-[100] min-w-[120px] animate-fade-in'>
                  <ul className='text-[#173861]'>
                    {languages.map(({ code, label, flag }) => (
                      <li key={code}>
                        <div
                          className="flex items-center gap-2"
                          onMouseEnter={() => setHoveredLang(code)}
                          onMouseLeave={() => setHoveredLang(null)}
                        >
                          <button
                            onClick={() => switchLanguage(code)}
                            className='block px-4 py-2 hover:bg-emerald-50 w-full text-left flex items-center gap-2 rounded-lg transition'
                          >
                            {hoveredLang === code
                              ? <div className="w-[26px] h-[26px] rounded-md overflow-hidden flex items-center justify-center bg-white">
                                  <img src={flag} alt={`${label} flag`} className="w-full h-full object-cover" />
                                </div>
                              : label}
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <a
              href="https://www.linkedin.com/company/105688845"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="hover:scale-110 transition"
            >
              <div className="w-[32px] h-[32px] rounded-full overflow-hidden flex items-center justify-center bg-white border border-gray-200 shadow-sm">
                <img
                  src="/assets/flags/linked.png"
                  alt="LinkedIn"
                  className="w-full h-full object-cover"
                />
              </div>
            </a>
            <a
              href="https://www.instagram.com/infisolarrazlog/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="hover:scale-110 transition"
            >
              <div className="w-[32px] h-[32px] rounded-full overflow-hidden flex items-center justify-center bg-white border border-gray-200 shadow-sm">
                <img
                  src="/assets/flags/insta.png"
                  alt="Instagram"
                  className="w-full h-full object-cover"
                />
              </div>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;